import {
  AvatarGroup,
  Box,
  Flex,
  Input,
  Popover,
  Portal,
  Skeleton,
  Text,
  VStack,
} from '@chakra-ui/react';
import { parseAsString, useQueryState } from 'nuqs';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FilterButton,
  FilterButtonAction,
  FilterButtonActiveOptions,
  FilterButtonLabel,
  FilterButtonMenu,
  FilterButtonMenuHeader,
  FilterMenuItem,
} from '@/components/filters/filter-button';
import { Avatar } from '@/components/ui/avatar';
import { useCreatorsQuery } from '@/hooks/queries/use-creators-query';
import { useDebounce } from '@/hooks/useDebounce';

/** One selectable person in the filter popover. */
export interface FilterPersonOption {
  id: string;
  /** Main row line (e.g. "@username" or a display name). */
  label: string;
  /** Optional muted second line (e.g. "{n} collabs"). */
  sublabel?: string;
  avatarUrl: string | null;
  /** Name feeding the avatar initials fallback; defaults to `label`. */
  avatarName?: string;
}

export interface FilterPersonOptionsSource {
  options: FilterPersonOption[];
  isLoading: boolean;
}

interface CreatorFilterProps {
  paramPrefix?: string;
  /** Full URL param override; wins over `paramPrefix`. */
  paramName?: string;
  /** i18n key for the button label and popover title. */
  labelKey?: string;
  /** i18n key for the popover's inner search placeholder. */
  searchPlaceholderKey?: string;
  /** i18n key for the empty options state. */
  emptyKey?: string;
  /**
   * Injectable options source. A HOOK: it must be render-stable (module-level
   * function, never inline) so the rules of hooks hold. Receives the debounced
   * popover search. Defaults to the discovery creators dataset.
   */
  useOptionsSource?: (search: string) => FilterPersonOptionsSource;
}

/** Default source: the discovery creators dataset, rendered as @username rows. */
const useCreatorOptionsSource = (search: string): FilterPersonOptionsSource => {
  const { data: creatorsResponse, isLoading } = useCreatorsQuery({
    search: search || undefined,
    page: 1,
    perPage: 50,
  });

  const options = useMemo<FilterPersonOption[]>(
    () =>
      (creatorsResponse?.data || []).map((creator) => ({
        id: creator.id,
        label: `@${creator.platformUsername}`,
        avatarUrl: creator.profilePictureUrl,
        avatarName: creator.name || creator.platformUsername,
      })),
    [creatorsResponse]
  );

  return { options, isLoading };
};

/**
 * Multi-select person filter on the shared filter-button primitives: popover
 * with a debounced inner search, avatar rows, checkbox selection, Cancel/Apply
 * and Clear all. The selection is comma-joined URL state. Generic over its
 * data source so other surfaces (e.g. the collab recipient filter) reuse the
 * exact UX by injecting a source hook instead of forking the component; the
 * default source keeps existing callers unchanged.
 */
export const CreatorFilter: React.FC<CreatorFilterProps> = ({
  paramPrefix = '',
  paramName,
  labelKey = 'mentions.filters.creator',
  searchPlaceholderKey = 'mentions.filters.creatorSearchPlaceholder',
  emptyKey = 'mentions.filters.noCreatorsAvailable',
  useOptionsSource = useCreatorOptionsSource,
}) => {
  const { t } = useTranslation();

  const creatorIdsParam = paramName ?? (paramPrefix ? `${paramPrefix}CreatorIds` : 'creatorIds');
  const [urlCreatorIds, setUrlCreatorIds] = useQueryState(creatorIdsParam, parseAsString);

  const selectedCreatorIds = useMemo(
    () => (urlCreatorIds ? urlCreatorIds.split(',').filter(Boolean) : []),
    [urlCreatorIds]
  );

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 250);

  const { options, isLoading } = useOptionsSource(debouncedSearch);

  const [tempCreatorIds, setTempCreatorIds] = useState<string[]>(() => selectedCreatorIds);
  const lastUrlRef = useRef(urlCreatorIds);
  // Cache the selected options so their avatars/labels still render when they
  // fall out of the current search page.
  const [knownOptions, setKnownOptions] = useState<Record<string, FilterPersonOption>>({});

  useEffect(() => {
    if (lastUrlRef.current === urlCreatorIds) return;
    lastUrlRef.current = urlCreatorIds;
    setTempCreatorIds(selectedCreatorIds);
  }, [urlCreatorIds, selectedCreatorIds]);

  useEffect(() => {
    if (options.length === 0) return;
    setKnownOptions((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const option of options) {
        if (!selectedCreatorIds.includes(option.id) && !prev[option.id]) continue;
        if (!prev[option.id]) {
          next[option.id] = option;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [options, selectedCreatorIds]);

  const handleApply = useCallback(() => {
    const next = tempCreatorIds.length > 0 ? tempCreatorIds.slice().sort().join(',') : null;
    const current =
      selectedCreatorIds.length > 0 ? selectedCreatorIds.slice().sort().join(',') : null;
    if (next === current) return;
    setUrlCreatorIds(tempCreatorIds.length > 0 ? tempCreatorIds.join(',') : null);
  }, [tempCreatorIds, selectedCreatorIds, setUrlCreatorIds]);

  const handleClear = useCallback(() => {
    setTempCreatorIds([]);
    setUrlCreatorIds(null);
    setSearch('');
  }, [setUrlCreatorIds]);

  const handleCreatorSelectionToggle = (creatorId: string) => {
    setTempCreatorIds((prevIds) =>
      prevIds.includes(creatorId)
        ? prevIds.filter((id) => id !== creatorId)
        : [...prevIds, creatorId]
    );
  };

  const isFilterActive = selectedCreatorIds.length > 0;

  const renderActiveOptions = () => (
    <Flex align="center" gap={2}>
      <AvatarGroup gap="0" spaceX="-3" size="2xs">
        {selectedCreatorIds.slice(0, 3).map((id) => {
          const option = knownOptions[id];
          return (
            <Avatar
              key={id}
              size="2xs"
              src={option?.avatarUrl ?? undefined}
              name={option?.avatarName || option?.label || ''}
            />
          );
        })}
      </AvatarGroup>
      {selectedCreatorIds.length > 3 && `+${selectedCreatorIds.length - 3}`}
    </Flex>
  );

  return (
    <FilterButton isFilterActive={isFilterActive} onClearFilter={handleClear}>
      <FilterButtonAction>
        <FilterButtonLabel>{t(labelKey)}</FilterButtonLabel>
        {isFilterActive && (
          <FilterButtonActiveOptions>{renderActiveOptions()}</FilterButtonActiveOptions>
        )}
      </FilterButtonAction>
      <Portal>
        <Popover.Positioner>
          <FilterButtonMenu onApply={handleApply} maxWidth="280px">
            <FilterButtonMenuHeader title={t(labelKey)} onClearAll={handleClear} />
            <Box p={3}>
              <Input
                size="sm"
                mb={2}
                placeholder={t(searchPlaceholderKey)}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
              {isLoading ? (
                <Box>
                  <Skeleton height="30px" mb={1} />
                  <Skeleton height="30px" mb={1} />
                  <Skeleton height="30px" />
                </Box>
              ) : (
                <VStack gap={0.5} align="stretch" maxHeight="300px" overflowY="auto" pr={1}>
                  {options.length === 0 && (
                    <Text fontSize="sm" color="dark" textAlign="center" py={2}>
                      {t(emptyKey)}
                    </Text>
                  )}
                  {options.map((option) => (
                    <FilterMenuItem
                      key={option.id}
                      onClick={() => handleCreatorSelectionToggle(option.id)}
                      isSelected={tempCreatorIds.includes(option.id)}
                    >
                      <Flex align="center" gap={2} minW={0}>
                        <Avatar
                          size="xs"
                          src={option.avatarUrl ?? undefined}
                          name={option.avatarName || option.label}
                        />
                        <Box minW={0}>
                          <Text fontSize="sm" fontWeight="medium" lineClamp={1}>
                            {option.label}
                          </Text>
                          {option.sublabel && (
                            <Text fontSize="xs" color="fg.muted" lineClamp={1}>
                              {option.sublabel}
                            </Text>
                          )}
                        </Box>
                      </Flex>
                    </FilterMenuItem>
                  ))}
                </VStack>
              )}
            </Box>
          </FilterButtonMenu>
        </Popover.Positioner>
      </Portal>
    </FilterButton>
  );
};
